import { Operation } from 'domain/entity/analysisOperation';
import { resolveInternalTranslationKey } from 'asset/translation/resolveInternalTranslationKey';
import { MAGNITUDE } from 'domain/entity/magnitude';
import { WidgetConfigurationField } from 'domain/entity/widget';
import { JsonObject, JsonProperty } from 'typescript-json-serializer';

export enum PARENT_ANALYSIS_TYPE {
    PRESET_PAGE = 'PRESET_PAGE',
    STORED_ANALYSIS = 'STORED_ANALYSIS',
    DASHBOARD_ANALYSIS = 'DASHBOARD',
}

export enum VISUALISATION_TYPE {
    RAW = 'RAW',
    COMBINED = 'COMBINED',
    INDICATOR = 'INDICATOR',
    HEATMAP = 'HEATMAP',
    MULTI_DEVICE_HEATMAP = 'MULTI_DEVICE_HEATMAP',
    SCATTER_PLOT = 'SCATTER_PLOT',
    TABLE = 'TABLE',
    STATISTICS = 'STATISTICS',
    AI_GENERATED_TEXT = 'AI_GENERATED_TEXT',
}

export enum COMBINED_SUBTYPE {
    DEFAULT = 'DEFAULT',
    LINE = 'LINE',
    COLUMN = 'COLUMN',
}

export enum COMBINED_SUBTYPE_SUPLEMENTARY {
    STACKED = 'STACKED',
    NOT_STACKED = 'NOT_STACKED',
    AREA = 'AREA',
}

export enum INDICATOR_SUBTYPE {
    PIE = 'PIE',
    KPI = 'KPI',
    GAUGE = 'GAUGE',
    COLUMN = 'COLUMN',
}

export enum LINE_BORDER {
    SOLID = 'SOLID',
    DASHED = 'DASHED',
    DOTTED = 'DOTTED',
}

export enum LOWER_IS {
    BETTER = 'BETTER',
    WORSE = 'WORSE',
    SAME = 'SAME',
}

export enum ALIGNMENT_OPTION {
    NONE = 'NONE',
    WEEKDAY = 'WEEKDAY',
}

export enum MAGNITUDE_SCALING {
    AUTOMATIC = 'AUTOMATIC',
    MANUAL = 'MANUAL',
}

export type MagnitudePreference = {
    magnitude: MAGNITUDE;
    units: string;
    scaling?: MAGNITUDE_SCALING;
    min?: number | null;
    max?: number | null;
};

export type VisualisationOverride = {
    combinedSubtype: COMBINED_SUBTYPE;
    fillArea: boolean;
    stack: boolean;
};

@JsonObject()
export class AnalysisPreset {
    constructor(
        @JsonProperty({ required: true }) readonly key: string,
        @JsonProperty({ required: true }) readonly fields: WidgetConfigurationField[],
        @JsonProperty({ required: false }) readonly visualisationOverride?: VisualisationOverride
    ) {}
}

@JsonObject()
export class ParentAnalysis {
    constructor(
        @JsonProperty({ required: true }) readonly id: string,
        @JsonProperty({ required: true }) readonly type: PARENT_ANALYSIS_TYPE
    ) {}
}

@JsonObject()
export class Visualisation {
    constructor(
        @JsonProperty({ required: true }) readonly type: VISUALISATION_TYPE,
        @JsonProperty({ required: false }) readonly combinedSubtype?: COMBINED_SUBTYPE,
        @JsonProperty({ required: false }) readonly stack?: boolean,
        @JsonProperty({ required: false }) readonly fillArea?: boolean,
        @JsonProperty({ required: false }) readonly indicatorSubtype?: INDICATOR_SUBTYPE,
        @JsonProperty({ required: false }) readonly reducerOperation?: Operation,
        @JsonProperty({ required: false }) readonly accumulatorOperation?: Operation | null,
        @JsonProperty({ required: false }) readonly invertAxes?: boolean,
        @JsonProperty({ required: false }) readonly magnitudePreferences?: MagnitudePreference[],
        @JsonProperty({ required: false }) readonly userPrompt?: string,
        @JsonProperty({ required: false }) readonly enableAiDescription?: boolean
    ) {}

    static aiGeneratedText(userPrompt: string): Visualisation {
        return new Visualisation(
            VISUALISATION_TYPE.AI_GENERATED_TEXT,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            userPrompt
        );
    }
}

@JsonObject()
export class AtomChartVisualisation {
    constructor(
        @JsonProperty({ required: false }) readonly type: string,
        @JsonProperty({ required: false }) readonly lineBorder: LINE_BORDER
    ) {}
}

@JsonObject()
export class AtomVisualisation {
    constructor(
        @JsonProperty({ required: true }) readonly atomId: string,
        @JsonProperty({ required: true }) readonly chart: AtomChartVisualisation
    ) {}
}

@JsonObject()
export class AtomComparison {
    constructor(
        @JsonProperty({ required: true }) readonly compareId: string,
        @JsonProperty({ required: true }) readonly againstId: string,
        @JsonProperty({ required: true }) readonly lowerIs: LOWER_IS,
        @JsonProperty({ required: false }) readonly marginPercentage?: number
    ) {}
}

@JsonObject()
class Analysis {
    constructor(
        @JsonProperty({ required: true }) readonly id: string,
        @JsonProperty({ required: true }) readonly period: string,
        @JsonProperty({ required: true }) readonly resolution: string,
        @JsonProperty({ required: true }) readonly compareTimeshift: string,
        @JsonProperty({ required: true }) readonly presets: AnalysisPreset[],
        @JsonProperty({ required: true }) readonly datasources: [],
        @JsonProperty({ required: true }) readonly formulas: [],
        @JsonProperty({ required: true }) readonly parent: ParentAnalysis,
        @JsonProperty({ required: true }) readonly visualisation: Visualisation,
        @JsonProperty({ required: false }) readonly atomsVisualisation: AtomVisualisation[],
        @JsonProperty({ required: false }) readonly comparisons: AtomComparison[],
        @JsonProperty({ required: true }) readonly compareAlignment: ALIGNMENT_OPTION,
        @JsonProperty({ required: true, afterDeserialize: resolveInternalTranslationKey }) readonly title: string
    ) {}
}

export default Analysis;
